import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import { Text } from '@atlaskit/primitives/compiled/text';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { Approver } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ApprovalsProgressBar } from '@confluence/approvals/entry-points/ApprovalsProgressBar.ts';

import { ApprovalStatusButton } from './ApprovalStatusButton.tsx';

const styles = cssMap({
	fullWidth: {
		width: '100%',
	},
	progressRow: {
		width: '192px',
	},
	textContainer: {
		flexShrink: 0,
		textAlign: 'start',
	},
	approvedTextContainer: {
		textAlign: 'start',
		maxWidth: '192px',
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
	},
	progressBarContainer: {
		flexGrow: 1,
		minWidth: '0px',
	},
});

const i18n = defineMessages({
	inProgressApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-title',
		defaultMessage: 'Approval in progress',
		description: 'Title for indicating the status of the approval process',
	},
	inProgressApprovalDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.in-progress-description',
		defaultMessage: '{approvalCount}/{approverCount} approved',
		description:
			'Description for indicating how many approvers have approved the content. approvalCount is the number of approvals received, approverCount is the total number of approvals required.',
	},
	approvedApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.approved-approval-title',
		defaultMessage: 'Approved by',
		description: 'Title for indicating the document approvers',
	},
	approvedApprovalDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.approved-approval-description',
		defaultMessage: '{approver} and {count} {count, plural, one {other} other {others}}',
		description:
			'Description for indicating how many approvers have approved the content in the case of multiple. {approver} is the name of the approver, {count} is the number of other approvers where count is greater than 1.',
	},
	declinedApprovalTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.declined-approval-title',
		defaultMessage: 'Approval declined',
		description: 'Title for indicating the status of the approval process',
	},
	changesRequestedTitle: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.changes-requested-title',
		defaultMessage: 'Changes requested by',
		description: 'Title for indicating that approvers have requested changes on the content',
	},
	openApprovalDetails: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.open-approval-details',
		defaultMessage: 'Open approval details: {title}, {description}',
		description:
			'Accessible label for the button that opens approval details. {title} is the approval status and {description} provides status details.',
	},
	openApprovalDetailsWithoutDescription: {
		id: 'content-state.contentStateInfoPopup.approvalStatusButton.open-approval-details-without-description',
		defaultMessage: 'Open approval details: {title}',
		description:
			'Accessible label for the button that opens approval details when no status description is available. {title} is the approval status.',
	},
});

export enum InProgressApprovalButtonVariant {
	IN_PROGRESS = 'in-progress',
	APPROVED = 'approved',
	DECLINED = 'declined',
	CHANGES_REQUESTED = 'changes-requested',
}

export interface InProgressApprovalButtonProps {
	onClick?: () => void;
	approvalCount: number;
	approverCount: number;
	approvers: Approver[];
	variant?: InProgressApprovalButtonVariant;
}

type FormatMessage = (
	message: MessageDescriptor,
	values?: Record<string, string | number>,
) => string;

const getApprovedDescription = (approvers: Approver[], formatMessage: FormatMessage): string => {
	if (approvers.length === 1) {
		return approvers[0].name;
	}

	return formatMessage(i18n.approvedApprovalDescription, {
		approver: approvers[0].name,
		count: approvers.length - 1,
	});
};

const getChangesRequestedDescription = (
	approvers: Approver[],
	formatMessage: FormatMessage,
): string => {
	const deniedApprovers = approvers.filter((a) => a.status === 'REVIEWER_DENIED');

	if (deniedApprovers.length === 0) {
		return '';
	}

	if (deniedApprovers.length === 1) {
		return deniedApprovers[0].name;
	}

	return formatMessage(i18n.approvedApprovalDescription, {
		approver: deniedApprovers[0].name,
		count: deniedApprovers.length - 1,
	});
};

const getTitle = (
	variant: InProgressApprovalButtonVariant,
	formatMessage: FormatMessage,
): string => {
	switch (variant) {
		case InProgressApprovalButtonVariant.IN_PROGRESS:
			return formatMessage(i18n.inProgressApprovalTitle);
		case InProgressApprovalButtonVariant.APPROVED:
			return formatMessage(i18n.approvedApprovalTitle);
		case InProgressApprovalButtonVariant.CHANGES_REQUESTED:
			return formatMessage(i18n.changesRequestedTitle);
		case InProgressApprovalButtonVariant.DECLINED:
		default:
			return formatMessage(i18n.declinedApprovalTitle);
	}
};

const getDescription = (
	variant: InProgressApprovalButtonVariant,
	approvers: Approver[],
	approvalCount: number,
	approverCount: number,
	formatMessage: FormatMessage,
): string => {
	switch (variant) {
		case InProgressApprovalButtonVariant.IN_PROGRESS:
		case InProgressApprovalButtonVariant.DECLINED:
			return formatMessage(i18n.inProgressApprovalDescription, { approvalCount, approverCount });
		case InProgressApprovalButtonVariant.CHANGES_REQUESTED:
			return getChangesRequestedDescription(approvers, formatMessage);
		case InProgressApprovalButtonVariant.APPROVED:
		default:
			return getApprovedDescription(approvers, formatMessage);
	}
};

export const InProgressApprovalButton = ({
	onClick,
	approvalCount,
	approverCount,
	approvers,
	variant = InProgressApprovalButtonVariant.IN_PROGRESS,
}: InProgressApprovalButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const title = getTitle(variant, formatMessage);

	const description = getDescription(
		variant,
		approvers,
		approvalCount,
		approverCount,
		formatMessage,
	);

	return (
		<ApprovalStatusButton
			onClick={onClick}
			approvers={approvers}
			ariaLabel={formatMessage(
				description ? i18n.openApprovalDetails : i18n.openApprovalDetailsWithoutDescription,
				description ? { title, description } : { title },
			)}
		>
			<Flex direction="column" alignItems="start" gap="space.025" xcss={styles.fullWidth}>
				<Text size="small" color="color.text.subtlest">
					{title}
				</Text>
				<Flex direction="row" gap="space.100" alignItems="center" xcss={styles.progressRow}>
					<Box
						xcss={
							variant === InProgressApprovalButtonVariant.APPROVED ||
							variant === InProgressApprovalButtonVariant.CHANGES_REQUESTED
								? styles.approvedTextContainer
								: styles.textContainer
						}
					>
						<Text size="medium" weight="medium">
							{description}
						</Text>
					</Box>
					{variant === InProgressApprovalButtonVariant.IN_PROGRESS && (
						<Box xcss={styles.progressBarContainer}>
							<ApprovalsProgressBar />
						</Box>
					)}
				</Flex>
			</Flex>
		</ApprovalStatusButton>
	);
};
